import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { Link, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';
import type { CustomerDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { useCustomer } from './api';
import { customerPath, customerTitle } from './parts';

/** What the layout hands to the page below it. */
export interface CustomerContext {
  customer: CustomerDto;
}
export const useCustomerContext = () => useOutletContext<CustomerContext>();

/**
 * Frame of every `/customers/:id/*` page: the customer's title with the inline actions
 * "Modify" and the status link, and the customer menu (the reference's customer items of the
 * left menu, plus its "Quick Links"). Each entry is shown only with its permission.
 */
export function CustomerLayout() {
  const { id } = useParams();
  const { can } = useAuth();
  const { pathname } = useLocation();
  const customer = useCustomer(id);
  const featureOn = useFeatureReleased();

  if (customer.isError)
    return <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />;
  if (!customer.data) return <LoadingState label="Loading customer…" />;

  const c = customer.data;
  const base = customerPath(c.id);
  const pages = [
    { label: 'Customer Profile', code: 'PC56', path: base },
    { label: 'Billing and Invoice', code: 'PC75', path: `${base}/billing` },
    { label: 'Order History', code: 'PC76', path: `${base}/orders` },
    { label: 'Purchase History', code: 'PC58', path: `${base}/purchases` },
    { label: 'Tickets', code: 'PC87', path: `${base}/tickets` },
    { label: 'Customer Activity Log', code: 'PC770', path: `${base}/activity` },
    { label: 'Manage Saved Card', code: 'PC900', path: `${base}/cards` },
    { label: 'Resend Notification', code: 'PC930', path: `${base}/notifications` },
  ].filter((p) => can(p.code));
  // The reference's eleven "Quick Links", in its order.
  const quickLinks = [
    { label: 'Add Topup - Minute/Text', code: 'PC60', path: `${base}/topup` },
    { label: 'Add Data Topup', code: 'PC64', path: `${base}/data-topup` },
    { label: 'Plan Change', code: 'PC77', path: `${base}/plan-change` },
    {
      label: 'Change Account Type',
      code: 'PC648',
      path: `${base}/account-type`,
      feature: 'change-account-type',
    },
    { label: 'Plan Renew', code: 'PC519', path: `${base}/plan-renew` },
    { label: 'Add Product', code: 'PC945', path: `${base}/add-product`, feature: 'add-product' },
    { label: 'Equipment Purchase', code: 'PC69', path: `${base}/equipment` },
    { label: 'Usage', code: 'PC254', path: `${base}/usage` },
    // Opens Modify on its Features and Services tab.
    { label: 'Features', code: 'PC92', path: `${base}/modify?tab=features` },
    // The Ports screen, opened for this customer.
    {
      label: 'Existing Subscriber Portin',
      code: 'PC343',
      path: `/ports/in/existing-subscriber?customer=${c.customerId}`,
    },
    { label: 'Auto Pay', code: 'PC574', path: `${base}/autopay` },
  ].filter(
    (q: { code: string; feature?: string }) => can(q.code) && (!q.feature || featureOn(q.feature)),
  );
  // Status and Modify are reached from the header and Quick Links, so no tab is selected there.
  const current = pages.find((p) => p.path === pathname.replace(/\/$/, ''))?.path ?? false;

  return (
    <Box>
      <PageHeader
        title={customerTitle(c)}
        actions={
          <>
            {can('PC401') ? (
              <Button component={Link} to={`${base}/modify`} variant="outlined">
                Modify (PC401)
              </Button>
            ) : null}
            {can('PC95') ? (
              <Button component={Link} to={`${base}/status`} variant="outlined">
                {c.status} (PC95)
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusChip status={c.status} />
              </Box>
            )}
          </>
        }
      />

      <Box
        component="nav"
        aria-label="Customer menu"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs value={current} variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1 }}>
          {pages.map((p) => (
            <Tab
              key={p.path}
              value={p.path}
              label={`${p.label} (${p.code})`}
              component={Link}
              to={p.path}
            />
          ))}
        </Tabs>
        {quickLinks.length ? (
          // With many links the row breaks between links, never inside one.
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Quick Links</Typography>
            {quickLinks.map((q) => (
              <Button key={q.path} component={Link} to={q.path} sx={{ whiteSpace: 'nowrap' }}>
                {q.label} ({q.code})
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>

      <Outlet context={{ customer: c } satisfies CustomerContext} />
    </Box>
  );
}
